import { ChevronLeft, MessageCircle } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { fmtRub, usePricing } from '../hooks/usePricing'
import { useTelegram } from '../hooks/useTelegram'
import WebApp from '@twa-dev/sdk'

const COMMANDS = [
  {
    title: 'Календарь',
    examples: [
      '«Запиши встречу с Андреем во вторник в 15:00» — добавлю',
      '«Когда я свободен на этой неделе?» — посмотрю и отвечу',
      'Синхронизирую с Google Calendar и Apple Calendar',
    ],
  },
  {
    title: 'Память',
    examples: [
      '«Запомни, что я работаю в Сбере»',
      '«Забудь про дедлайн в пятницу»',
      '«Что ты обо мне помнишь?»',
    ],
  },
  {
    title: 'Напоминания',
    examples: [
      '«Напомни завтра в 9 отправить отчёт»',
      '«Напомни через час позвонить маме»',
      '«Напомни в пятницу к стоматологу»',
      '«Напомни когда проснусь позвонить маме» — напомню при первой активности после сна',
    ],
  },
  {
    title: 'Задачи',
    examples: [
      '«Создай задачу купить билеты»',
      '«Запиши: подготовить презентацию, высокий приоритет»',
      '«Покажи мои задачи»',
      'Задача со сроком встаёт в твой календарь, выполненная — уходит из него',
    ],
  },
  {
    title: 'События и календарь',
    examples: [
      '«Встреча с Андреем во вторник в 15:00»',
      '«Сделать КП завтра к вечеру» — назовёшь время, запишу событием',
      'Предупрежу за 15 минут до начала, о событиях из Apple и Google тоже',
      'События на весь день — напомню утром',
    ],
  },
  {
    title: 'Голос и файлы',
    examples: [
      'Отправь голосовое — Delo разберёт и ответит, в том числе голосом',
      'Отправь PDF/DOCX/TXT — Delo прочитает и ответит на вопросы',
    ],
  },
  {
    title: 'Telegram-чаты (Pro)',
    examples: [
      'Нахожу договорённости и дедлайны в твоих чатах',
      'Предлагаю поставить напоминание — ты подтверждаешь кнопкой',
      'Отвечаю за тебя когда занят — /auto_reply',
      'Расшифровываю голосовые и кружки прямо в переписке',
      'Показываю, что в чате удалили или переписали',
      'Загрузи архив переписки — извлеку факты из истории (до 10 000 сообщений)',
      'Всё это включается по отдельности: Профиль → Настройки → Чаты Telegram',
    ],
  },
  {
    title: 'Почта и календарь (Plus и выше)',
    examples: [
      '«Проверь почту»',
      '«Ответь Ивану, что согласен на пятницу»',
      '«Когда я свободен на этой неделе для часовой встречи?»',
      '«Забронируй встречу с Андреем во вторник в 14:00»',
    ],
  },
  {
    title: 'Яндекс Почта (Plus и выше)',
    examples: [
      '«Покажи непрочитанные письма»',
      '«Найди письма от Ивана за последний месяц»',
      '«Ответь Марине, что документы вышлю завтра»',
    ],
  },
  {
    title: 'Apple Calendar (Plus и выше)',
    examples: [
      'Подключи через /connect — буду читать и создавать события',
      '«Когда моя следующая встреча?»',
      '«Добавь встречу завтра в 15:00»',
    ],
  },
  {
    title: 'Веб-поиск (Plus и выше)',
    examples: [
      '«Найди, что происходит с курсом доллара»',
      '«Какая погода в Москве на выходных?»',
    ],
  },
]

const FAQ = [
  {
    q: 'Что такое Delo?',
    a: 'Личный ассистент в Telegram. Помогает с делами и вопросами, запоминает важное, ведёт календарь и напоминает вовремя — голосом или текстом.',
  },
  {
    q: 'Это бесплатно?',
    // {price} — цена самого дешёвого платного тарифа, приходит с бэкенда.
    a: 'Базовая версия бесплатна: 5 сообщений в день, календарь, напоминания и задачи. Платные тарифы начинаются от {price} в месяц — больше сообщений, почта, поиск в интернете, автоматический поиск договорённостей.',
  },
  {
    q: 'Нужно ли что-то устанавливать?',
    a: 'Нет. Delo работает прямо в Telegram — напиши боту @DeloAppBot.',
  },
  {
    q: 'Чем Pro отличается от Plus?',
    a: 'На Plus Delo делает то, о чём ты просишь: находит письмо, добавляет встречу, отвечает на вопрос.\n\nНа Pro Delo работает сам — читает входящие письма и подключённые чаты, находит договорённости и дедлайны, напоминает о них без запроса. Плюс утренний разбор дня.',
  },
  {
    q: 'Как работает чтение чатов?',
    a: 'На тарифе Pro Delo видит новые сообщения в чатах, которые ты выбрал сам. Находит договорённости и сроки, предлагает поставить напоминание — решение остаётся за тобой.\n\nИстория переписки до момента подключения не обрабатывается. Требуется Telegram Premium.',
  },
  {
    q: 'Безопасно ли подключать почту?',
    a: 'Доступ выдаётся через официальный протокол OAuth, токены хранятся в зашифрованном виде. Отключить можно в любой момент — через настройки Delo или через настройки Google.\n\nПолные тексты писем не сохраняются: Delo обрабатывает их, чтобы найти нужное, и хранит только результат.',
  },
  {
    q: 'Что Delo помнит обо мне?',
    a: 'То, что ты попросил запомнить, и то, что извлёк из разговора сам — например, где ты работаешь или как зовут твоих коллег. Всё это видно в разделе «Память», любой факт можно удалить.',
  },
  {
    q: 'Как работает календарь?',
    a: 'Delo ведёт свой календарь и синхронизирует его с Google Calendar и Apple Calendar в обе стороны. Скажи голосом, что и когда — добавлю встречу. Изменения в любом из календарей появятся во всех.',
  },
  {
    q: 'Можно поставить напоминание без времени?',
    a: 'Да. Скажи «напомни, когда проснусь» — напомню при первой активности после длительной паузы. Если к полудню не появишься, напомню всё равно.',
  },
  {
    q: 'Что происходит, когда заканчивается подписка?',
    a: 'Доступ к расширенной функциональности прекращается, тариф становится бесплатным. Данные сохраняются — память, напоминания, задачи остаются на месте. Подключённые сервисы отключаются, но их можно подключить снова после продления.',
  },
  {
    q: 'Автоматическое продление есть?',
    a: 'Нет. Подписка не продлевается сама — Delo напомнит, когда срок будет подходить к концу.',
  },
  {
    q: 'Можно вернуть деньги?',
    a: 'Да, в течение 14 дней после оплаты, если подписка не использовалась. Напиши на my@zakharovsem.ru — рассмотрим в течение 10 дней. Подробности в оферте.',
  },
  {
    q: 'Как удалить свои данные?',
    a: 'Отдельные факты, напоминания и историю можно удалить в приложении. Чтобы удалить всё вместе с аккаунтом — команда /delete. Данные удаляются в течение семи дней.',
  },
  {
    q: 'Delo может ошибаться?',
    a: 'Да. Ответы формируются автоматически и могут содержать неточности. Не стоит полагаться на них в вопросах медицины, права и финансов — там нужен специалист.',
  },
  {
    q: 'Как связаться с поддержкой?',
    a: 'Напиши @zakharovsemyon в Telegram или на my@zakharovsem.ru. Отвечаю быстро.',
  },
]

export function Help() {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  const { haptic } = useTelegram()
  // Цена в FAQ — с бэкенда, чтобы не разойтись с витриной подписки.
  const pricing = usePricing()
  const cheapestRub = pricing.plan('plus')?.rub
  const fillPrice = (text: string) =>
    text.replace('{price}', cheapestRub === undefined ? '…' : fmtRub(cheapestRub))

  const openSupport = () => {
    haptic.impactOccurred('light')
    try {
      WebApp.openTelegramLink('https://t.me/zakharovsemyon')
    } catch {
      window.open('https://t.me/zakharovsemyon', '_blank')
    }
  }

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <Link
          to="/profile"
          className="btn-active inline-flex items-center gap-1 text-sm text-ink-1 mb-2"
        >
          <ChevronLeft size={16} /> Профиль
        </Link>
        <p className="text-h1 text-ink">Помощь</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-6">
        {/* Что умеет Delo */}
        <section>
          <p className="section-label">Что умеет Delo</p>
          <div className="space-y-2">
            {COMMANDS.map(({ title, examples }) => (
              <div key={title} className="bg-surface-1 border border-line-1 rounded-lg p-4">
                <p className="text-base font-medium text-ink mb-2">{title}</p>
                <ul className="space-y-1">
                  {examples.map((ex, i) => (
                    <li key={i} className="text-sm text-ink-1 leading-relaxed">{ex}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <p className="section-label">Частые вопросы</p>
          <div className="bg-surface-1 border border-line-1 rounded-lg divide-y divide-line">
            {FAQ.map(({ q, a }, idx) => {
              const open = openIdx === idx
              return (
                <button
                  key={idx}
                  onClick={() => setOpenIdx(open ? null : idx)}
                  className="btn-active w-full p-4 text-left transition-all"
                  style={open
                    ? { borderLeft: '3px solid var(--color-accent)', paddingLeft: '13px' }
                    : undefined}
                >
                  <p className={`text-base ${open ? 'text-ink font-medium' : 'text-ink'}`}>
                    {q}
                  </p>
                  {open && (
                    <p className="text-sm text-ink-1 leading-relaxed mt-2 whitespace-pre-line">{fillPrice(a)}</p>
                  )}
                </button>
              )
            })}
          </div>
        </section>

        {/* Поддержка */}
        <section>
          <p className="section-label">Связаться с поддержкой</p>
          <div className="bg-surface-1 border border-line-1 rounded-lg p-4 space-y-3">
            <p className="text-base text-ink-1 leading-relaxed">
              Напиши автору Delo — отвечаю быстро. Расскажи о проблеме, идее или просто что не нравится.
            </p>
            <Button
              variant="accent"
              fullWidth
              icon={<MessageCircle size={18} />}
              onClick={openSupport}
            >
              Написать в Telegram
            </Button>
          </div>
        </section>
      </div>
    </div>
  )
}
